import { ThemeStatusBadge } from '@/components/attractions/themes';
import type { CrudConfig } from '@startupquickstart/frontend';
import { Palette } from 'lucide-react';
import { Link } from 'react-router-dom';

export interface ThemeTypeTheme {
  id: string;
  attractionId: string;
  themeTypeId: string;
  name?: string | null;
  description?: string | null;
  longDescription?: string | null;
  status: 'draft' | 'published' | 'needs_review';
  createdAt: string;
  updatedAt: string;
  attraction?: {
    id: string;
    name: string;
  };
  themeType?: {
    id: string;
    name: string;
    slug: string;
    tone?: string | null;
  };
  featuredImage?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
}

/**
 * Factory function to create theme type themes config for a specific theme type
 */
export const createThemeTypeThemesConfig = (themeTypeId: string): CrudConfig<ThemeTypeTheme> => ({
  entityName: 'Theme',
  entityNamePlural: 'Themes',

  endpoints: {
    list: `/theme-types/${themeTypeId}/themes`,
    // Single theme details are read from the attraction themes endpoint
    single: (id: string, item?: ThemeTypeTheme) => {
      if (item?.attractionId) {
        return `/attractions/${item.attractionId}/themes/${id}`;
      }
      return `/theme-types/${themeTypeId}/themes/${id}`;
    },
    create: `/theme-types/${themeTypeId}/themes`,
    update: (id: string, item?: ThemeTypeTheme) => {
      if (item?.attractionId) {
        return `/attractions/${item.attractionId}/themes/${id}`;
      }
      return `/theme-types/${themeTypeId}/themes/${id}`;
    },
    delete: (id: string, item?: ThemeTypeTheme) => {
      if (item?.attractionId) {
        return `/attractions/${item.attractionId}/themes/${id}`;
      }
      return `/theme-types/${themeTypeId}/themes/${id}`;
    },
  },

  fields: [
    {
      key: 'attraction',
      label: 'Attraction',
      type: 'custom',
      required: false,
      format: (_value: ThemeTypeTheme['attraction'], fullRecord?: ThemeTypeTheme) => {
        if (!fullRecord?.attraction) return '—';
        return (
          <Link
            to={`/attractions/${fullRecord.attraction.id}`}
            className="text-primary hover:underline"
            onClick={e => e.stopPropagation()}
          >
            {fullRecord.attraction.name}
          </Link>
        );
      },
    },
    {
      key: 'name',
      label: 'Theme Name',
      type: 'text',
      required: false,
      format: (value: string | null) => {
        return value || '—';
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Needs Review', value: 'needs_review' },
      ],
      format: (value: ThemeTypeTheme['status']) => {
        return <ThemeStatusBadge status={value} />;
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['attraction', 'name', 'status', 'createdAt'],
    searchableFields: ['name', 'description'],
    sortableFields: ['createdAt', 'status'],
    defaultSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    emptyState: {
      icon: <Palette className="h-12 w-12" />,
      title: 'No themes yet',
      description: 'No attractions have been themed with this theme type yet.',
    },
    actions: {
      create: {
        enabled: false,
      },
      view: {
        enabled: true,
        getUrl: (theme: ThemeTypeTheme) => {
          return `/attractions/${theme.attractionId}/themes/${theme.id}`;
        },
      },
      edit: {
        enabled: false,
      },
      delete: {
        enabled: false,
      },
    },
  },

  detailView: {
    sections: [],
  },

  form: {
    sections: [],
  },
});
